import { useEffect, useRef } from 'react'
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  sendEmailVerification,
} from 'firebase/auth'
import type { UserInfo } from 'firebase/auth'
import * as firebaseui from 'firebaseui'
import { auth } from '@/config/firebase'
import 'firebaseui/dist/firebaseui.css'

interface FirebaseAuthUIProps {
  className?: string
}

export function FirebaseAuthUI({ className }: FirebaseAuthUIProps) {
  const elementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Get or create FirebaseUI instance
    const ui =
      firebaseui.auth.AuthUI.getInstance() || new firebaseui.auth.AuthUI(auth)

    const uiConfig: firebaseui.auth.Config = {
      callbacks: {
        signInSuccessWithAuthResult: (authResult) => {
          // If this is a new user who used email/password, send verification email
          if (authResult.additionalUserInfo?.isNewUser) {
            // Check if this is an email/password sign-up by looking at provider data
            const isEmailPasswordSignUp =
              authResult.user.providerData.some(
                (provider: UserInfo) =>
                  provider.providerId === EmailAuthProvider.PROVIDER_ID
              ) && !authResult.user.emailVerified

            if (isEmailPasswordSignUp) {
              void sendEmailVerification(authResult.user, {
                url: `${window.location.origin}/verify-email?verified=1`,
                handleCodeInApp: false,
              })
                .then(() => {
                  console.log('Verification email sent successfully')
                })
                .catch((error) => {
                  console.error('Failed to send verification email:', error)
                })
            }
          }
          return false
        },
        uiShown: () => {
          // Hide the loader
          const loader = document.getElementById('firebaseui-auth-loader')
          if (loader) {
            loader.style.display = 'none'
          }

          // Update provider button labels to reflect combined sign in/sign up flow
          const updateProviderButtonLabels = () => {
            const container = elementRef.current
            if (!container) return

            const setButtonLabel = (providerId: string, newLabel: string) => {
              const buttons = container.querySelectorAll<HTMLButtonElement>(
                `button[data-provider-id="${providerId}"]`
              )
              buttons.forEach((button) => {
                button.setAttribute('aria-label', newLabel)
                const longSpan = button.querySelector<HTMLElement>(
                  '.firebaseui-idp-text-long'
                )
                const shortSpan = button.querySelector<HTMLElement>(
                  '.firebaseui-idp-text-short'
                )
                const textSpan = button.querySelector<HTMLElement>(
                  '.firebaseui-idp-text'
                )
                ;[longSpan, shortSpan, textSpan].forEach((el) => {
                  if (el) el.textContent = newLabel
                })
              })
            }

            setButtonLabel('password', 'Continue with Email')
            setButtonLabel('google.com', 'Continue with Google')
          }

          // Run immediately and on next tick to catch late DOM updates
          updateProviderButtonLabels()
          setTimeout(updateProviderButtonLabels, 0)
        },
      },
      signInFlow: 'popup', // Use popup for better UX
      signInOptions: [
        // Email/Password - let FirebaseUI handle sign-in vs sign-up routing
        {
          provider: EmailAuthProvider.PROVIDER_ID,
          requireDisplayName: false,
        },
        // Google
        {
          provider: GoogleAuthProvider.PROVIDER_ID,
          scopes: ['profile', 'email'],
          customParameters: {
            // Forces account selection even when one account is available
            prompt: 'select_account',
          },
        },
      ],
      tosUrl: '/terms',
      privacyPolicyUrl: '/privacy',
      credentialHelper: firebaseui.auth.CredentialHelper.GOOGLE_YOLO,
    }

    // Start the UI if element is available
    if (elementRef.current) {
      try {
        // Check if UI is already rendered to avoid double rendering
        if (
          !elementRef.current.hasChildNodes() ||
          elementRef.current.children.length === 0
        ) {
          ui.start(elementRef.current, uiConfig)
        }
      } catch (error) {
        console.error('Error starting FirebaseUI:', error)
        // If there's an error, try to reset and restart
        ui.reset()
        setTimeout(() => {
          if (elementRef.current) {
            ui.start(elementRef.current, uiConfig)
          }
        }, 100)
      }
    }

    // Cleanup function
    return () => {
      try {
        // Reset the UI but don't delete the instance
        ui.reset()
      } catch (error) {
        console.warn('Error during FirebaseUI cleanup:', error)
      }
    }
  }, [])

  return (
    <div className={`firebase-auth-ui ${className || ''}`}>
      <style>{`
        .firebase-auth-ui .firebaseui-container {
          background-color: transparent !important;
          box-shadow: none !important;
          max-width: none !important;
        }
        .firebase-auth-ui .firebaseui-card-content {
          background-color: transparent !important;
          box-shadow: none !important;
        }
        .firebase-auth-ui .firebaseui-form-container {
          background-color: transparent !important;
          box-shadow: none !important;
        }
        .firebase-auth-ui .firebaseui-idp-list {
          margin-bottom: 2rem !important;
        }
        .firebase-auth-ui .firebaseui-idp-password .firebaseui-idp-button,
        .firebase-auth-ui .firebaseui-idp-button[data-provider-id="password"],
        .firebase-auth-ui button[data-provider-id="password"],
        .firebase-auth-ui .mdl-button--colored {
          background-color: #396362 !important;
          border: 1px solid #396362 !important;
        }
        .firebase-auth-ui .firebaseui-idp-password .firebaseui-idp-button:hover,
        .firebase-auth-ui .firebaseui-idp-button[data-provider-id="password"]:hover,
        .firebase-auth-ui button[data-provider-id="password"]:hover,
        .firebase-auth-ui .mdl-button--colored:hover {
          background-color: #2d4f4e !important;
          border: 1px solid #2d4f4e !important;
        }
      `}</style>
      <div id="firebaseui-auth-loader" className="py-4 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
      <div ref={elementRef} id="firebaseui-auth-container"></div>
    </div>
  )
}
